import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Container, TextField, IconButton, Paper,
  Avatar, Chip, CircularProgress, Fade, Button, Divider,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import GavelIcon from '@mui/icons-material/Gavel';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BalanceIcon from '@mui/icons-material/Balance';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function GiudiceSessionePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { nome, tribunale } = (location.state || {}) as { nome: string; tribunale: string };

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [profilo, setProfilo] = useState<{ nSentenze: number; citta: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Gestione trial e lista attesa
  const [trialUsed, setTrialUsed] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Redirect se arrivato senza stato
  useEffect(() => {
    if (!nome) { navigate('/booky-giudice'); return; }

    // Carica profilo
    axios.get(`/api/giudici/profilo?nome=${encodeURIComponent(nome)}&tribunale=${encodeURIComponent(tribunale || '')}`)
      .then(r => setProfilo({ nSentenze: r.data.nSentenze, citta: r.data.citta }))
      .catch(() => {});

    // Messaggio di benvenuto automatico
    const welcome: Message = {
      role: 'assistant',
      content: `Ciao, sono BOOKY, l'assistente AI di ITF Plus.\n\nHo analizzato le sentenze reali emesse da ${nome} presso il ${tribunale}, presenti nel nostro database. Sulla base di questo studio sono in grado di fornirti una valutazione su come questo magistrato ha affrontato casi analoghi al tuo.\n\n⚠️ Attenzione: non sono il magistrato, né lo rappresento. Sono un'AI che ha studiato il suo storico giurisprudenziale e può offrirti indicazioni professionali basate sui suoi precedenti reali.\n\nDescrivimi il tuo caso: le parti coinvolte, i fatti rilevanti e cosa stai cercando di valutare. Farò un'analisi comparativa con i precedenti documentati.`,
      timestamp: new Date(),
    };
    setMessages([welcome]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const inviaMessaggio = async () => {
    if (!input.trim() || loading) return;

    const userMsg: Message = { role: 'user', content: input.trim(), timestamp: new Date() };
    const nuoviMessages = [...messages, userMsg];
    setMessages(nuoviMessages);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        '/api/giudici/chat',
        {
          nome,
          tribunale,
          messages: nuoviMessages
            .filter(m => m.role !== 'assistant' || nuoviMessages.indexOf(m) > 0)
            .map(m => ({ role: m.role, content: m.content })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assistantMsg: Message = {
        role: 'assistant',
        content: res.data.risposta,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      // Blocca dopo il primo scambio
      setTrialUsed(true);
    } catch (err: any) {
      const detail = err?.response?.data?.message || 'Errore sconosciuto';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Si è verificato un errore nella sessione: ${detail}. Riprovate.`,
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const inviaEmail = async () => {
    if (!emailInput.trim() || emailLoading) return;
    setEmailLoading(true);
    try {
      await axios.post('/api/giudici/lista-attesa', {
        email: emailInput.trim(),
        giudice: nome,
        tribunale,
      });
      setEmailSent(true);
    } catch {
      setEmailSent(true); // mostra successo comunque
    } finally {
      setEmailLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); inviaMessaggio(); }
  };

  if (!nome) return null;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0d1b2a 0%, #111e2e 100%)',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── HEADER ── */}
      <Box sx={{
        background: 'linear-gradient(90deg, #0a1520, #1a2f4a)',
        borderBottom: '1px solid rgba(184,134,11,0.25)',
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <IconButton onClick={() => navigate('/booky-giudice')} sx={{ color: 'rgba(245,230,192,0.5)', '&:hover': { color: '#B8860B' } }}>
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'linear-gradient(135deg, #B8860B, #DAA520)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 12px rgba(184,134,11,0.4)',
        }}>
          <GavelIcon sx={{ color: '#0d1b2a', fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#F5E6C0', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
            BOOKY — Analisi Giudice
          </Typography>
          <Typography sx={{ color: 'rgba(245,230,192,0.45)', fontSize: 12 }}>
            Studio su {nome} · {tribunale}
            {profilo && ` · ${profilo.nSentenze} sentenze analizzate`}
          </Typography>
        </Box>

        <Chip
          icon={<BalanceIcon sx={{ fontSize: '14px !important' }} />}
          label="Analisi AI — non è il giudice"
          size="small"
          sx={{
            background: 'rgba(184,134,11,0.15)',
            color: '#B8860B',
            border: '1px solid rgba(184,134,11,0.3)',
            fontSize: 11,
          }}
        />
      </Box>

      {/* ── AVVISO CONTESTUALE ── */}
      <Box sx={{
        background: 'rgba(184,134,11,0.06)',
        borderBottom: '1px solid rgba(184,134,11,0.12)',
        px: 3, py: 1, textAlign: 'center',
      }}>
        <Typography sx={{ color: 'rgba(184,134,11,0.7)', fontSize: 11 }}>
          🤖 BOOKY analizza lo storico giurisprudenziale di {nome} basandosi sulle sentenze reali nel database ITF Plus. Non è il magistrato né lo rappresenta. Solo a scopo professionale informativo.
        </Typography>
      </Box>

      {/* ── MESSAGGI ── */}
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1, md: 4 }, py: 3 }}>
        <Container maxWidth="md" disableGutters>
          {messages.map((msg, idx) => (
            <Fade in key={idx}>
              <Box sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 3,
                gap: 1.5,
                alignItems: 'flex-start',
              }}>
                {msg.role === 'assistant' && (
                  <Avatar sx={{
                    background: 'linear-gradient(135deg, #B8860B, #DAA520)',
                    width: 36, height: 36, flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(184,134,11,0.3)',
                  }}>
                    <GavelIcon sx={{ fontSize: 18, color: '#0d1b2a' }} />
                  </Avatar>
                )}

                <Box sx={{ maxWidth: '76%' }}>
                  {msg.role === 'assistant' && (
                    <Typography sx={{ color: '#B8860B', fontSize: 11, fontWeight: 700, mb: 0.5, letterSpacing: '0.06em' }}>
                      BOOKY · Analisi {nome}
                    </Typography>
                  )}

                  <Paper sx={{
                    p: 2,
                    background: msg.role === 'assistant'
                      ? 'linear-gradient(135deg, rgba(26,47,74,0.9), rgba(20,35,58,0.95))'
                      : 'linear-gradient(135deg, rgba(184,134,11,0.25), rgba(218,165,32,0.2))',
                    border: msg.role === 'assistant'
                      ? '1px solid rgba(184,134,11,0.2)'
                      : '1px solid rgba(184,134,11,0.4)',
                    borderRadius: msg.role === 'assistant' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
                    '& p': { color: msg.role === 'assistant' ? '#F5E6C0' : '#fff', fontSize: 14, lineHeight: 1.7, m: 0, mb: 1, '&:last-child': { mb: 0 } },
                    '& strong': { color: msg.role === 'assistant' ? '#fbbf24' : '#fff', fontWeight: 700 },
                    '& em': { color: msg.role === 'assistant' ? 'rgba(245,230,192,0.7)' : 'rgba(255,255,255,0.8)', fontStyle: 'italic' },
                    '& ul, & ol': { color: msg.role === 'assistant' ? '#F5E6C0' : '#fff', fontSize: 14, lineHeight: 1.7, pl: 2.5, mb: 1 },
                    '& li': { mb: 0.5 },
                    '& h1, & h2, & h3': { color: msg.role === 'assistant' ? '#fbbf24' : '#fff', fontFamily: 'Syne, sans-serif', fontWeight: 700, mt: 1.5, mb: 0.5, fontSize: 15 },
                    '& hr': { borderColor: 'rgba(184,134,11,0.2)', my: 1.5 },
                    '& blockquote': { borderLeft: '3px solid rgba(184,134,11,0.4)', pl: 1.5, ml: 0, color: 'rgba(245,230,192,0.7)', fontStyle: 'italic' },
                  }}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Paper>

                  <Typography sx={{ color: 'rgba(245,230,192,0.25)', fontSize: 10, mt: 0.5, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                    {msg.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>

                {msg.role === 'user' && (
                  <Avatar sx={{ background: 'rgba(255,255,255,0.1)', width: 36, height: 36, flexShrink: 0 }}>
                    <AccountCircleIcon sx={{ fontSize: 20, color: 'rgba(245,230,192,0.6)' }} />
                  </Avatar>
                )}
              </Box>
            </Fade>
          ))}

          {loading && (
            <Fade in>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                <Avatar sx={{ background: 'linear-gradient(135deg, #B8860B, #DAA520)', width: 36, height: 36 }}>
                  <GavelIcon sx={{ fontSize: 18, color: '#0d1b2a' }} />
                </Avatar>
                <Paper sx={{
                  p: 2, px: 3,
                  background: 'rgba(26,47,74,0.9)',
                  border: '1px solid rgba(184,134,11,0.2)',
                  borderRadius: '4px 16px 16px 16px',
                }}>
                  <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                    {[0, 1, 2].map(i => (
                      <Box key={i} sx={{
                        width: 7, height: 7, borderRadius: '50%', background: '#B8860B',
                        animation: 'pulse 1.4s ease-in-out infinite',
                        animationDelay: `${i * 0.2}s`,
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '50%': { opacity: 1, transform: 'scale(1)' },
                        },
                      }} />
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Fade>
          )}

          <div ref={messagesEndRef} />
        </Container>
      </Box>

      {/* ── INPUT / LISTA ATTESA ── */}
      <Box sx={{
        borderTop: '1px solid rgba(184,134,11,0.2)',
        background: 'rgba(13,27,42,0.98)',
        px: { xs: 2, md: 4 }, py: 2,
      }}>
        <Container maxWidth="md" disableGutters>

          {!trialUsed ? (
            <>
              {/* Suggerimenti rapidi solo al primo messaggio */}
              {messages.length === 1 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                'Come tende a decidere sui casi di risarcimento danni?',
                'Qual è il suo orientamento documentato sui contratti di locazione?',
                'Come ha valutato le eccezioni di prescrizione nelle sentenze analizzate?',
              ].map(s => (
                    <Chip
                      key={s} label={s} size="small" clickable
                      onClick={() => setInput(s)}
                      sx={{
                        background: 'rgba(184,134,11,0.1)',
                        border: '1px solid rgba(184,134,11,0.25)',
                        color: 'rgba(245,230,192,0.7)', fontSize: 11,
                        '&:hover': { background: 'rgba(184,134,11,0.2)', color: '#F5E6C0' },
                      }}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth multiline maxRows={4}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Descrivi il caso: BOOKY lo analizzerà confrontandolo con i precedenti del magistrato..."
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#F5E6C0',
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(184,134,11,0.25)' },
                      '&:hover fieldset': { borderColor: 'rgba(184,134,11,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#B8860B' },
                    },
                    '& .MuiInputBase-input::placeholder': { color: 'rgba(245,230,192,0.3)' },
                  }}
                />
                <IconButton
                  onClick={inviaMessaggio}
                  disabled={!input.trim() || loading}
                  sx={{
                    background: input.trim() && !loading ? 'linear-gradient(135deg, #B8860B, #DAA520)' : 'rgba(255,255,255,0.06)',
                    color: input.trim() && !loading ? '#0d1b2a' : 'rgba(245,230,192,0.3)',
                    width: 46, height: 46, borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.05)' },
                    '&.Mui-disabled': { background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: '#B8860B' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
              <Typography sx={{ color: 'rgba(245,230,192,0.2)', fontSize: 10, mt: 1, textAlign: 'center' }}>
                Invio con Invio · A capo con Shift+Invio
              </Typography>
            </>
          ) : (
            /* ── FORM LISTA ATTESA ── */
            <Fade in>
              <Box sx={{
                background: 'linear-gradient(135deg, rgba(184,134,11,0.08), rgba(218,165,32,0.05))',
                border: '1px solid rgba(184,134,11,0.3)',
                borderRadius: 3, p: 3,
              }}>
                {!emailSent ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #B8860B, #DAA520)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <GavelIcon sx={{ color: '#0d1b2a', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#F5E6C0', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
                          Hai utilizzato la sessione di prova gratuita
                        </Typography>
                        <Typography sx={{ color: 'rgba(245,230,192,0.5)', fontSize: 12 }}>
                          Per accedere a BOOKY Giudice completo, entra nella lista d'attesa
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(184,134,11,0.15)', mb: 2 }} />

                    <Typography sx={{ color: 'rgba(245,230,192,0.7)', fontSize: 13, mb: 2, lineHeight: 1.6 }}>
                      🎯 <strong style={{ color: '#DAA520' }}>BOOKY Giudice</strong> è il primo strumento AI che analizza le sentenze
                      reali di ogni giudice italiano. Lascia la tua email per essere tra i primi ad accedere.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        type="email"
                        placeholder="La tua email professionale..."
                        value={emailInput}
                        onChange={e => setEmailInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') inviaEmail(); }}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#F5E6C0',
                            background: 'rgba(255,255,255,0.04)',
                            borderRadius: 2,
                            '& fieldset': { borderColor: 'rgba(184,134,11,0.3)' },
                            '&:hover fieldset': { borderColor: 'rgba(184,134,11,0.6)' },
                            '&.Mui-focused fieldset': { borderColor: '#B8860B' },
                          },
                          '& .MuiInputBase-input::placeholder': { color: 'rgba(245,230,192,0.35)' },
                        }}
                      />
                      <Button
                        onClick={inviaEmail}
                        disabled={!emailInput.trim() || emailLoading}
                        sx={{
                          background: 'linear-gradient(135deg, #B8860B, #DAA520)',
                          color: '#0d1b2a',
                          fontWeight: 800,
                          fontSize: 13,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          textTransform: 'none',
                          boxShadow: '0 2px 12px rgba(184,134,11,0.35)',
                          '&:hover': { background: 'linear-gradient(135deg, #DAA520, #B8860B)', transform: 'translateY(-1px)' },
                          '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        {emailLoading ? <CircularProgress size={16} sx={{ color: '#0d1b2a' }} /> : 'Entra in lista →'}
                      </Button>
                    </Box>

                    <Typography sx={{ color: 'rgba(245,230,192,0.3)', fontSize: 10, mt: 1.5 }}>
                      Nessuno spam. Solo la notifica quando il tuo accesso è disponibile.
                    </Typography>
                  </>
                ) : (
                  <Fade in>
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography sx={{ fontSize: 32, mb: 1 }}>⚖️</Typography>
                      <Typography sx={{ color: '#DAA520', fontWeight: 800, fontSize: 18, mb: 0.5 }}>
                        Sei in lista d'attesa!
                      </Typography>
                      <Typography sx={{ color: 'rgba(245,230,192,0.6)', fontSize: 13 }}>
                        Ti contatteremo a <strong style={{ color: '#F5E6C0' }}>{emailInput}</strong> appena il servizio sarà disponibile.
                      </Typography>
                      <Typography sx={{ color: 'rgba(245,230,192,0.35)', fontSize: 11, mt: 1.5 }}>
                        Nel frattempo esplora BOOKY Search e BOOKY Work ✨
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Fade>
          )}

        </Container>
      </Box>
    </Box>
  );
}
